<template>
  <div>
    <p class="text-muted mb-2">
      {{ selectedLeads.length }} lead<span v-if="selectedLeads.length !== 1">s</span> selected.
      Choose an employee to assign all of them.
    </p>
    <validation-observer ref="formRef">
      <b-form @submit.prevent="submit">
        <b-form-group label="Assign to" label-for="bulk-assign-employee">
          <validation-provider v-slot="{ errors }" rules="required" name="Employee">
            <v-select
              id="bulk-assign-employee"
              v-model="assignee"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="employeeOptions"
              :loading="loadingEmployees"
              placeholder="Search employee..."
              label="fullname"
              :reduce="(u) => u"
              :clearable="true"
              class="bulk-assign-vselect"
            >
              <template #option="{ profile_image, username, fullname }">
                <b-avatar :src="getprofileImage(profile_image)" size="32" class="mr-50" />
                <span class="font-weight-bolder">{{ fullname }}</span>
                <span class="text-muted">({{ username }})</span>
              </template>
              <template #selected-option="{ profile_image, username, fullname }">
                <b-avatar :src="getprofileImage(profile_image)" size="24" class="mr-50" />
                <span class="font-weight-bolder">{{ fullname }}</span>
                <span class="text-muted small">({{ username }})</span>
              </template>
            </v-select>
            <small class="text-danger d-block">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <b-form-group label="Remarks (optional)" label-for="bulk-assign-remarks">
          <b-form-input
            id="bulk-assign-remarks"
            v-model="transferremarks"
            placeholder="Optional note for this bulk assignment"
          />
        </b-form-group>

        <div class="d-flex justify-content-end gap-1 flex-wrap mt-2">
          <b-button variant="outline-secondary" @click="onCancel" :disabled="submitting">
            Cancel
          </b-button>
          <b-button variant="primary" type="submit" :disabled="submitting || selectedLeads.length === 0">
            <b-spinner v-if="submitting" small class="mr-50" />
            Assign {{ selectedLeads.length }} lead<span v-if="selectedLeads.length !== 1">s</span>
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import axios from "@/components/axios";
import vSelect from "vue-select";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { BAvatar, BButton, BForm, BFormGroup, BFormInput, BSpinner } from "bootstrap-vue";

export default {
  name: "BulkAssignLead",
  components: {
    vSelect,
    ValidationObserver,
    ValidationProvider,
    BAvatar,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
  },
  props: {
    selectedLeads: {
      type: Array,
      default: () => [],
    },
    closepopup: {
      type: Function,
      required: true,
    },
    getGridData: {
      type: Function,
      default: null,
    },
    afterAssign: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      accessToken: localStorage.getItem("accessToken"),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      imagePath: process.env.VUE_APP_IMAGE_PATH,
      employeeOptions: [],
      loadingEmployees: false,
      assignee: null,
      transferremarks: "",
      submitting: false,
    };
  },
  mounted() {
    this.loadEmployees();
  },
  methods: {
    getprofileImage(image) {
      if (image !== undefined && image !== null && image !== "") {
        return image.includes(this.imagePath) ? image : this.imagePath + image;
      }
      return image;
    },
    async loadEmployees() {
      this.loadingEmployees = true;
      try {
        const res = await axios({
          method: "GET",
          url: `${this.baseApi}/getEmployee`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
        });
        const list = res.data && res.data.data;
        this.employeeOptions = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error(e);
        this.$swal({
          title: "Error",
          text: "Could not load employees. Try again or contact support.",
          icon: "error",
          customClass: { confirmButton: "btn btn-primary" },
          buttonsStyling: false,
        });
      } finally {
        this.loadingEmployees = false;
      }
    },
    onCancel() {
      this.closepopup();
    },
    async submit() {
      const ok = await this.$refs.formRef.validate();
      if (!ok || !this.assignee || !this.assignee.id) {
        return;
      }
      if (!this.selectedLeads.length) {
        return;
      }

      const leadIds = this.selectedLeads.map((r) => r.id).filter((id) => id != null);
      if (!leadIds.length) {
        this.$swal({
          title: "Invalid selection",
          text: "Could not read lead ids from the selection.",
          icon: "warning",
          customClass: { confirmButton: "btn btn-primary" },
          buttonsStyling: false,
        });
        return;
      }

      this.submitting = true;
      try {
        await axios({
          method: "POST",
          url: `${this.baseApi}/bulk-assign-leads`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
          data: JSON.stringify({
            lead_ids: leadIds,
            assign_to: this.assignee.id,
            transferremarks: this.transferremarks || "",
          }),
        });

        this.$swal({
          title: "Done",
          text: `${leadIds.length} lead(s) assigned successfully.`,
          icon: "success",
          customClass: { confirmButton: "btn btn-primary" },
          buttonsStyling: false,
        });

        if (typeof this.getGridData === "function") {
          this.getGridData();
        }
        if (typeof this.afterAssign === "function") {
          this.afterAssign();
        }
        this.closepopup();
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Assignment failed.";
        this.$swal({
          title: "Error",
          text: typeof msg === "string" ? msg : "Assignment failed.",
          icon: "error",
          customClass: { confirmButton: "btn btn-primary" },
          buttonsStyling: false,
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.bulk-assign-vselect {
  min-width: 100%;
}
</style>
